import { isSection } from '@tunasong/models'
import type { Section } from '@tunasong/models'
import type { TunaEditor } from '../plugin-types.js'
import { Editor } from '../slate-typescript.js'

export interface ResolvedSectionBlock {
  /** Stable Plate element id of the enclosing section. */
  id: string
  /** Plate element type. Always 'section' in v1; reserved for future 'line' support. */
  type: 'section'
  /** Short summary — section title when available. */
  summary?: string
}

/**
 * Resolve the cursor's enclosing section block. Used by palette commands
 * derived from BlockAffordance declarations to compute focus at
 * invocation time without requiring the caller to plumb editor state.
 *
 * Returns null when no selection or when the cursor isn't inside a
 * section. Callers' palette commands then return null too, which the
 * palette runner treats as disabled-when-not-applicable (the existing
 * convention; see today's tunabrain-next-line command).
 *
 * Implemented against plugin-lib's own Editor wrapper rather than
 * SectionQueries.inSection (which lives in @tunasong/editor) to avoid
 * a circular dependency: @tunasong/editor already depends on
 * @tunasong/plugin-lib. Mirrors the SectionQueries.currentRange shape
 * (Editor.above with isSection match).
 */
export const resolveSectionAtCursor = (editor: TunaEditor): ResolvedSectionBlock | null => {
  if (!editor.selection) {
    return null
  }
  const entry = Editor.above<Section>(editor, { match: node => isSection(node) })
  if (!entry) {
    return null
  }
  const [node] = entry
  if (typeof node.id !== 'string') {
    return null
  }
  const title = (node as { title?: unknown }).title
  const summary = typeof title === 'string' && title.length > 0 ? title : undefined
  return { id: node.id, type: 'section', summary }
}
